/**
 * Links inside a subsite.
 *
 * Router links get the /~slug prefix from the router's basename. Raw HTML in
 * page text does not, so its root-relative page links are prefixed here, on the
 * server and the client alike so hydration matches. Static files, the API, the
 * admin and other subsites keep their own addresses.
 *
 * Pure, shared by the server and the public site.
 */

import { SUBSITE_PREFIX } from './address.js';

// First path segments that are never pages.
const STATIC_SEGMENTS = new Set([
    'images', '_next', 'assets', 'brand', 'fonts', 'shared', 'api', 'admin', 'styles', 'font-awesome',
]);

/**
 * Whether a root-relative href points at a page of the site.
 * @param {string} href - Starts with "/"
 * @returns {boolean}
 */
export function isPageHref(href) {
    if (!href.startsWith('/') || href.startsWith('//')) return false;
    if (href.startsWith(`/${SUBSITE_PREFIX}`)) return false;
    const path = href.split(/[?#]/)[0];
    const segments = path.split('/').filter(Boolean);
    if (segments.length && STATIC_SEGMENTS.has(segments[0])) return false;
    // A file in the assets folder (/cv.pdf), but not a media route (/work/a.img).
    const last = segments[segments.length - 1] || '';
    if (/\.[a-z0-9]+$/i.test(last) && !/\.(?:img|vid|txt)$/i.test(last)) return false;
    return true;
}

/**
 * Prefix root-relative page links in an HTML string with the subsite basename.
 * @param {string} html
 * @param {string} [basename] - e.g. /~spring-2026; nothing changes without one
 * @returns {string}
 */
export function prefixInternalHrefs(html, basename) {
    if (!html || !basename) return html;
    return html.replace(/(\shref\s*=\s*)(["'])(\/[^"']*)\2/gi, (match, attr, quote, href) => {
        if (!isPageHref(href)) return match;
        const inner = href === '/' ? '' : (href.startsWith('/?') || href.startsWith('/#') ? href.slice(1) : href);
        return `${attr}${quote}${basename}${inner}${quote}`;
    });
}

/**
 * The path inside the subsite, for handing to a router that adds the basename
 * back. Paths outside the basename come back unchanged.
 * @param {string} path
 * @param {string} [basename]
 * @returns {string}
 */
export function stripBasename(path, basename) {
    if (!basename || typeof path !== 'string') return path;
    if (path === basename) return '/';
    const next = path.charAt(basename.length);
    if (path.startsWith(basename) && (next === '/' || next === '?' || next === '#')) {
        const rest = path.slice(basename.length);
        return rest.startsWith('/') ? rest : `/${rest}`;
    }
    return path;
}

export default { isPageHref, prefixInternalHrefs, stripBasename };
